import { z } from 'zod';
import { brandCodeSchema } from './enums.js';
import { dateish } from './events.js';
/**
 * Transakční zprávy z e-shopu (D-193 až D-209, viz DECISIONS). Registr kódů `T` je jediný zdroj
 * pravdy pro endpoint `POST /v1/transactional-messages` i pro dokumentaci
 * `docs/API-TRANSAKCNI-ZPRAVY.md`. Kódy přesouvané průchodem (`transactional-passthrough.ts`)
 * mají stejná čísla (D-190).
 */
const optionalText = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .optional()
    .or(z.literal('').transform(() => undefined));
const text = (max) => z.string().trim().min(1).max(max);
const money = z.number().finite().nonnegative();
const currency = z.string().regex(/^[A-Z]{3}$/, 'Tříznakový kód měny velkými písmeny');
export const httpsUrl = z
    .string()
    .max(2000)
    .url()
    .refine((v) => v.startsWith('https://'), 'Jen adresa https');
const itemPart = z.object({
    name: text(300),
    qty: z.number().int().positive(),
    unit_price: money,
    total_price: money.optional(),
    category: optionalText(100),
    race_id: optionalText(100),
});
const orderPart = z.object({
    order_code: text(100),
    created_at: dateish,
    paid_at: dateish.optional(),
    currency,
    total: money,
    subtotal: money.optional(),
    shipping: money.optional(),
    discount: money.optional(),
    payment_method_label: text(100),
    order_url: httpsUrl,
    tickets_url: httpsUrl.optional(),
    delivery: z.object({ method_label: optionalText(100), address: optionalText(1000) }).optional(),
    items: z.array(itemPart).min(1).max(100),
});
const paymentPart = z.object({
    bank_account: text(100),
    iban: optionalText(64),
    variable_symbol: text(32),
    amount: money,
    currency: currency.optional(),
    due_date: dateish,
    qr_url: httpsUrl.optional(),
});
const def = (d) => [d.code, d];
const none = z.object({});
export const TX_CODES = Object.fromEntries([
    def({
        code: 'T01',
        name: 'Potvrzení zaplacené objednávky',
        shopTemplate: 'OrderPaid',
        templateBase: 'JRN_S01_potvrzeni',
        journeyCode: 'S01',
        validForMinutes: 120,
        status: 'active',
        schema: z.object({ order: orderPart.extend({ paid_at: dateish }) }),
    }),
    def({
        code: 'T02',
        name: 'Objednávka vytvořena — platba převodem',
        shopTemplate: 'OrderCreatedWithBankTransfer',
        templateBase: 'TX_T02_prevod',
        validForMinutes: 120,
        status: 'active',
        schema: z.object({ order: orderPart, payment: paymentPart }),
    }),
    def({
        code: 'T03',
        name: 'Upomínka nezaplacené objednávky',
        shopTemplate: 'UnpaidOrderReminder',
        templateBase: 'TX_T03_upominka',
        validForMinutes: 360,
        status: 'active',
        schema: z.object({
            order: orderPart,
            payment: paymentPart,
            reminder_index: z.number().int().positive().optional(),
            cancel_at: dateish.optional(),
        }),
    }),
    def({
        code: 'T04',
        name: 'Vstupenky odeslány',
        shopTemplate: 'ShippingTicketsOld',
        templateBase: 'TX_T04_odeslano',
        validForMinutes: 120,
        status: 'active',
        schema: z.object({
            order: orderPart,
            dispatched_at: dateish.optional(),
            tracking_url: httpsUrl.optional(),
        }),
    }),
    def({
        code: 'T05',
        name: 'Hlídací pes (nahrazuje zpráva M06)',
        shopTemplate: 'WatchdogTicketsAvailable',
        templateBase: 'TX_T05_hlidaci-pes',
        validForMinutes: 120,
        status: 'replaced',
        schema: none,
    }),
    def({
        code: 'T06',
        name: 'Odpověď zákaznické péče',
        shopTemplate: 'SupportReply',
        templateBase: 'TX_T06_odpoved-pece',
        validForMinutes: 1440,
        status: 'active',
        schema: z.object({
            reply: z.object({
                text: text(20000),
                case_ref: optionalText(100),
                agent_name: optionalText(200),
                subject: optionalText(150),
            }),
        }),
    }),
    def({
        code: 'T07',
        name: 'Potvrzení přijetí dotazu',
        shopTemplate: 'ContactFormRecap',
        templateBase: 'TX_T07_dotaz',
        validForMinutes: 1440,
        status: 'active',
        schema: z.object({
            form: z.object({
                message: text(10000),
                submitted_at: dateish,
                subject: optionalText(200),
            }),
        }),
    }),
    def({
        code: 'T08',
        name: 'Aktivace účtu',
        shopTemplate: 'CustomerActivate',
        templateBase: 'TX_T08_aktivace',
        validForMinutes: 20160,
        passthroughOnly: true,
        status: 'active',
        schema: none,
    }),
    def({
        code: 'T09',
        name: 'Obnova hesla',
        shopTemplate: 'CustomerResetPassword',
        templateBase: 'TX_T09_heslo',
        validForMinutes: 1440,
        passthroughOnly: true,
        status: 'active',
        schema: none,
    }),
    def({
        code: 'T10',
        name: 'Odeslání (rezervováno)',
        shopTemplate: 'Shipping',
        templateBase: 'TX_T10_odeslani',
        validForMinutes: 120,
        status: 'reserved',
        schema: none,
    }),
    def({
        code: 'T11',
        name: 'Flexi využito',
        shopTemplate: 'FlexiUsed',
        templateBase: 'TX_T11_flexi',
        validForMinutes: null,
        passthroughOnly: true,
        status: 'active',
        schema: none,
    }),
]);
/** Kódy, které API opravdu přijímá (včetně těch jen pro průchod). */
export const TX_ACTIVE_CODES = Object.values(TX_CODES)
    .filter((d) => d.status === 'active')
    .map((d) => d.code);
/** Kódy s datovým tvarem (vykreslení v hubu); T08, T09 a T11 existují jen v průchodu. */
export const TX_DATA_CODES = TX_ACTIVE_CODES.filter((c) => !TX_CODES[c]?.passthroughOnly);
export const TX_STATUSES = [
    'queued',
    'sending',
    'sent',
    'held',
    'shadow',
    'dry_run',
    'failed',
    'unknown',
    'cancelled',
];
export const TX_MODES = ['off', 'shadow', 'live'];
/** Kdo zprávu skládá: `hub` vlastní šablonou, `passthrough` šablonou e-shopu ve SmartEmailingu (D-187). */
export const TX_RENDERS = ['hub', 'passthrough'];
export const txEnvelopeSchema = z.object({
    idempotency_key: z
        .string()
        .regex(/^[A-Za-z0-9._:-]{8,200}$/, '8 až 200 znaků A-Z a-z 0-9 . _ : -'),
    brand: brandCodeSchema,
    code: z.string().min(1).max(64),
    language: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z]{2}(-[a-z]{2})?$/)
        .optional()
        .or(z.literal('').transform(() => undefined)),
    recipient: z.object({ email: z.string().email().max(254), name: optionalText(200) }),
    order_ref: optionalText(100),
    race_id: optionalText(100),
    send_after: dateish.optional(),
    data: z.record(z.unknown()),
});
const issues = (error, prefix) => error.issues.map((i) => ({
    path: [prefix, ...i.path].filter((p) => p !== '').join('.'),
    message: i.message,
}));
/** Ověří obálku i data podle kódu. Pořadí chyb: obálka → kód → rezervace/nahrazení → data. */
export function parseTxRequest(raw) {
    const env = txEnvelopeSchema.safeParse(raw);
    if (!env.success) {
        return { ok: false, status: 400, error: 'invalid_input', errors: issues(env.error, '') };
    }
    const codeDef = TX_CODES[env.data.code];
    if (!codeDef)
        return { ok: false, status: 400, error: 'unknown_code' };
    if (codeDef.status === 'reserved')
        return { ok: false, status: 409, error: 'code_reserved' };
    if (codeDef.status === 'replaced')
        return { ok: false, status: 409, error: 'code_replaced' };
    const data = codeDef.schema.safeParse(env.data.data);
    if (!data.success) {
        return { ok: false, status: 400, error: 'invalid_input', errors: issues(data.error, 'data') };
    }
    return { ok: true, request: env.data, data: data.data, def: codeDef };
}
/** Jen aktivní kódy; rezervované a nahrazené nemají šablonu ani přepínač. */
export function txRuleLike(code) {
    const d = TX_CODES[code];
    if (!d || d.status !== 'active')
        return null;
    return {
        code: d.code,
        name: d.name,
        channel: 'service',
        scope: 'transactional',
        sender: 'hub',
        templateKey: d.templateBase,
    };
}
//# sourceMappingURL=transactional.js.map